/**
 * Parse error message to Monaco Editor markers
 * @param {string} msg - Error message
 * @param {string} language - Language identifier
 * @returns {Array} Array of Monaco marker objects
 */
export const parseErrorToMarkers = (msg = "", language = "plaintext") => {
  const markers = [];
  const text = String(msg);

  const push = (line = 1, column = 1, message = text) =>
    markers.push({
      startLineNumber: Math.max(1, Number(line) || 1),
      startColumn: Math.max(1, Number(column) || 1),
      endLineNumber: Math.max(1, Number(line) || 1),
      endColumn: Math.max(2, Number(column) || 1) + 1,
      message,
      severity: 8, // monaco.MarkerSeverity.Error
    });

  // C#: "error CS0000: ..." or "Program.cs(12,5): error CS0000: ..."
  let m = text.match(/(\w+\.cs)\((\d+),(\d+)\)/);
  if (m) {
    push(Number(m[2]), Number(m[3]), text);
    return markers;
  }
  m = text.match(/error CS\d+:/);
  if (m) {
    // Try to find line number in surrounding context
    const lineMatch = text.match(/\((\d+),(\d+)\)/);
    if (lineMatch) {
      push(Number(lineMatch[1]), Number(lineMatch[2]), text);
      return markers;
    }
  }

  // Go: "main.go:12:5: ..." or "main.go:12: undefined: ..."
  m = text.match(/(\w+\.go):(\d+):(\d+):/);
  if (m) {
    push(Number(m[2]), Number(m[3]), text);
    return markers;
  }
  m = text.match(/(\w+\.go):(\d+):/);
  if (m) {
    push(Number(m[2]), 1, text);
    return markers;
  }

  // Rust: "error[E0000]: ..." or "src/main.rs:12:5: ..."
  m = text.match(/(\w+\.rs):(\d+):(\d+):/);
  if (m) {
    push(Number(m[2]), Number(m[3]), text);
    return markers;
  }
  m = text.match(/error\[E\d+\]:/);
  if (m) {
    const lineMatch = text.match(/:(\d+):(\d+):/);
    if (lineMatch) {
      push(Number(lineMatch[1]), Number(lineMatch[2]), text);
      return markers;
    }
  }

  // PHP: "Parse error: syntax error, unexpected ... in ... on line 12"
  m = text.match(/on line (\d+)/i);
  if (m) {
    push(Number(m[1]), 1, text);
    return markers;
  }
  m = text.match(/line (\d+)/i);
  if (m) {
    push(Number(m[1]), 1, text);
    return markers;
  }

  // Ruby: "...:12: syntax error, unexpected ..." or "...:12:5: ..."
  m = text.match(/:(\d+):(\d+):/);
  if (m) {
    push(Number(m[1]), Number(m[2]), text);
    return markers;
  }
  m = text.match(/:(\d+):\s*(syntax error|undefined method)/);
  if (m) {
    push(Number(m[1]), 1, text);
    return markers;
  }

  // Swift: "error: ...:12:5: ..."
  m = text.match(/error:.*?:(\d+):(\d+):/);
  if (m) {
    push(Number(m[1]), Number(m[2]), text);
    return markers;
  }

  // Kotlin: "error: ...: (12, 5) ..." or "...:12:5: error: ..."
  m = text.match(/\((\d+),\s*(\d+)\)/);
  if (m && text.includes("error")) {
    push(Number(m[1]), Number(m[2]), text);
    return markers;
  }
  m = text.match(/:(\d+):(\d+):\s*error:/);
  if (m) {
    push(Number(m[1]), Number(m[2]), text);
    return markers;
  }

  // Scala: Similar to Java
  m = text.match(/(\w+\.scala):(\d+):(\d+):/);
  if (m) {
    push(Number(m[2]), Number(m[3]), text);
    return markers;
  }

  // Java/C/C++: "...:12: error: ..."
  m = text.match(/:[ ]?(\d+):/);
  if (m) {
    push(Number(m[1]), 1, text);
    return markers;
  }

  // Java stack: "(Class.java:123)"
  m = text.match(/\((.+?):(\d+)\)/);
  if (m) {
    push(Number(m[2]), 1, text);
    return markers;
  }

  // Python: File "stdin", line 5 or "line 5, in <module>"
  m = text.match(/File ".*?", line (\d+)/);
  if (m) {
    push(Number(m[1]), 1, text);
    return markers;
  }
  m = text.match(/line (\d+), in/);
  if (m) {
    push(Number(m[1]), 1, text);
    return markers;
  }

  // Node/JavaScript: <anonymous>:12:5
  m = text.match(/<anonymous>:(\d+):(\d+)/);
  if (m) {
    push(Number(m[1]), Number(m[2]), text);
    return markers;
  }

  // TypeScript: Similar to JavaScript
  m = text.match(/(\w+\.ts):(\d+):(\d+)/);
  if (m) {
    push(Number(m[2]), Number(m[3]), text);
    return markers;
  }

  // SQL: "Error near line 12:" or "syntax error at line 12"
  m = text.match(/(?:line|at line)\s+(\d+)/i);
  if (m) {
    push(Number(m[1]), 1, text);
    return markers;
  }

  // Generic: Try to find any line number pattern
  m = text.match(/(?:line|Line|LINE)\s*[:\s]+(\d+)/);
  if (m) {
    push(Number(m[1]), 1, text);
    return markers;
  }

  // Fallback: put error on line 1
  push(1, 1, text);
  return markers;
};

/**
 * Apply error diagnostics to Monaco Editor
 * @param {object} editor - Monaco Editor instance
 * @param {object} monaco - Monaco namespace
 * @param {Array<string>} messages - Array of error messages
 * @param {string} language - Language identifier
 */
export const applyDiagnostics = (editor, monaco, messages = [], language = "plaintext") => {
  if (!editor || !monaco) return;
  const model = editor.getModel();
  const markers = messages.flatMap((msg) => parseErrorToMarkers(msg, language));
  monaco.editor.setModelMarkers(model, "judge", markers.slice(0, 200));
};

/**
 * Clear error diagnostics from Monaco Editor
 * @param {object} editor - Monaco Editor instance
 * @param {object} monaco - Monaco namespace
 */
export const clearDiagnostics = (editor, monaco) => {
  if (!editor || !monaco) return;
  monaco.editor.setModelMarkers(editor.getModel(), "judge", []);
};

